import {
	Component,
	ViewEncapsulation,
	ChangeDetectorRef,
	ViewChild,
	ElementRef,
	ChangeDetectionStrategy
} from '@angular/core';
import { IHeaderParams, PaginationChangedEvent, SelectionChangedEvent } from '@ag-grid-community/core';
import { IHeaderAngularComp } from '@ag-grid-community/angular';
import { MatIconModule } from '@angular/material/icon';
import { NgIf, NgClass } from '@angular/common';

import { DatacenterSelectionService } from '../../../../../shared/services/datacenter-selection.service';

interface MyParams extends IHeaderParams {
	icon_checked: string;
	icon_indeterminate: string;
	icon_unchecked: string;
	showCheckbox: boolean;
}

@Component({
	selector: 'copo-offer-add-to-cart-header',
	templateUrl: './add-to-cart-header.component.html',
	styleUrl: './add-to-cart-header.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		NgIf,
		NgClass,
		MatIconModule,
	],
})
export class AddToCartHeaderComponent implements IHeaderAngularComp {
	@ViewChild('menuButton', { read: ElementRef }) menuButton!: ElementRef<HTMLElement>;
	params?: MyParams;

	allSelected: 'checked' | 'indeterminate' | 'unchecked' = 'unchecked';
	remoteRowsSelected = false;
	sortingAscending = false;
	sortingDescending = false;
	oldRowCount = 0;

	constructor(
		readonly changeDetectorRef: ChangeDetectorRef,
		private readonly selectionService: DatacenterSelectionService,
	) {}

	agInit(params: MyParams) {
		this.params = params;

		const onSelectionChanged = (event?: SelectionChangedEvent<unknown>) => {
			if (event && event.source === 'api') {
				return;
			}
			let allSelected = true;
			let allUnselected = true;
			params.api.forEachNode(node => {
				if (!node.isSelected()) {
					allSelected = false;
				} else {
					allUnselected = false;
				}
			});
			const lastPage = params.api.paginationIsLastPageFound();
			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			this.allSelected = allUnselected && (!this.remoteRowsSelected || lastPage) ? 'unchecked'
				// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
				: allSelected && (this.remoteRowsSelected || lastPage)
					? 'checked' : 'indeterminate';
			this.changeDetectorRef.markForCheck();
		};

		this.updateSorting(params);
		params.api.addEventListener('selectionChanged', onSelectionChanged);

		params.column.addEventListener('sortChanged', () => {
			this.updateSorting(params);
		});
		params.column.addEventListener('filterChanged', () => {
			this.changeDetectorRef.markForCheck();
		});
		let lastRowCount = 0;
		params.api.addEventListener('paginationChanged', (ev: PaginationChangedEvent) => {
			this.oldRowCount = lastRowCount;
			lastRowCount = ev.api.getDisplayedRowCount();
			if (!this.remoteRowsSelected) {
				return;
			}
			ev.api.forEachNode(node => {
				if (node.rowIndex !== null && node.rowIndex < this.oldRowCount - 1) {
					return;
				}
				if (node.isSelected()) {
					node.setSelected(true, false);
				}
			});
		});

		onSelectionChanged();
	}

	updateSorting(params: MyParams) {
		const sort = params.column.getSort();
		this.sortingAscending = sort === 'asc';
		this.sortingDescending = sort === 'desc';
		this.changeDetectorRef.markForCheck();
	}

	refresh(params: MyParams) {
		this.agInit(params);
		return true;
	}

	onMenuClicked() {
		if (this.params) {
			this.params.showColumnMenu(this.menuButton.nativeElement);
		}
	}

	onSortRequested(event: MouseEvent | TouchEvent) {
		if (!this.params) {
			return;
		}
		if (!this.params.enableSorting) {
			return;
		}
		this.params.progressSort(event.shiftKey);
		this.changeDetectorRef.markForCheck();
	}

	select(event: MouseEvent | TouchEvent) {
		this.selectionService.isAllSelected = true;
		this.selectionService.deselectedNodes = [];
		this.selectionService.selectedNodes = [];
		event.stopPropagation();
		if (!this.params) {
			return;
		}
		this.allSelected = 'checked';
		this.remoteRowsSelected = true;
		this.params.api.forEachNode(node => {
			node.setSelected(true, false);
		});
		this.changeDetectorRef.markForCheck();
	}

	unselect(event: MouseEvent | TouchEvent) {
		this.selectionService.isAllSelected = false;
		this.selectionService.deselectedNodes = [];
		this.selectionService.selectedNodes = [];
		event.stopPropagation();
		if (!this.params) {
			return;
		}
		this.allSelected = 'unchecked';
		this.remoteRowsSelected = false;
		this.params.api.forEachNode(node => {
			node.setSelected(false, false);
		});
		this.changeDetectorRef.markForCheck();
	}

	headerAlignement() {
		return this.params?.column.getColDef().type === 'numericColumn';
	}
}
